/**
 * Per-channel shape of the Settings → Integrations DETAIL view.
 *
 * The detail view is deliberately NOT a fixed template with blank slots. Mora's
 * channel set spans organic social, commerce, web analytics and a file source —
 * an organic channel has no spend and no ad accounts, a file source has no
 * scheduled sync, and an analytics-only channel has no publishing. Rendering an
 * empty "Publishing" heading under Google Analytics would be a section that
 * exists to fill a grid, which is the opposite of honest.
 *
 * So each channel declares which sections it can truthfully fill. A `false`
 * here means the section is NOT RENDERED — never rendered empty.
 *
 * 🔴 Every flag below is a claim about the running system. Verified against
 * code AND live production rows on 2026-08-08:
 *   - `publishes`   → `src/lib/platforms/registry.ts` `publishable`, plus the
 *                     live `social_post_targets` ledger (which carries rows for
 *                     facebook / instagram / linkedin / tiktok and nothing else).
 *   - `syncMode`    → whether a platform has a scheduled fetcher, an explicit
 *                     manual refresh only, or neither. A platform must never
 *                     inherit scheduled-sync copy on the strength of a manual
 *                     endpoint — that rule is why `meta-ads` was `'none'`.
 *                     It now has a real recurring job: `sync-meta-ads-analytics-6h`
 *                     runs `sync_analytics_for_platform('meta-ads')`, and it
 *                     was OBSERVED writing on 2026-09-09 — connection
 *                     `last_sync` 18:30:20Z (the 6-hourly slot), both selected ad
 *                     accounts `sync_status: 'ready'`. Corrected to
 *                     `'scheduled'` on that evidence, not on the presence of a
 *                     cron row.
 *   - `dailyCoverage` → the platform writes day rows into
 *                     `account_metrics_daily` (the table the coverage strip
 *                     reads). YouTube keeps `true` even though it has never
 *                     written a row: the fetcher targets it, so "no days yet"
 *                     is the honest answer rather than hiding the question.
 */

export interface IntegrationDetailSpec {
  /** One line under the channel name. What this connection is FOR, not what it is. */
  description: string;
  /**
   * An example question the agent can answer BECAUSE this channel is connected.
   * Written for what the channel actually reports — never a generic
   * "tell me about my marketing". Omitted where the connection produces no
   * measured data to ask about (Google Drive).
   */
  agentPrompt?: string;
  /** Mora can publish content here. Drives the Publishing section. */
  publishes: boolean;
  /** How Mora can refresh this connection. Never infer scheduled from a manual endpoint. */
  syncMode: 'scheduled' | 'manual' | 'none';
  /** Writes day rows to `account_metrics_daily`. Drives the Data coverage strip. */
  dailyCoverage: boolean;
}

const SPECS: Record<string, IntegrationDetailSpec> = {
  instagram: {
    description: 'Publishes posts and reels, and reports how each one performed.',
    agentPrompt:
      'Which of my Instagram posts got the most reach in the last 30 days, and what did they have in common?',
    publishes: true,
    syncMode: 'scheduled',
    dailyCoverage: true,
  },
  facebook: {
    description: 'Publishes to your Page and reports Page reach, followers and engagement.',
    agentPrompt:
      'How did my Facebook Page perform last month, and which posts drove the engagement?',
    publishes: true,
    syncMode: 'scheduled',
    dailyCoverage: true,
  },
  linkedin: {
    description:
      'Publishes through selected personal profiles and Company Pages, with reporting where LinkedIn grants it.',
    agentPrompt:
      'Which of my LinkedIn posts drove the most engagement, and what did they have in common?',
    publishes: true,
    syncMode: 'scheduled',
    dailyCoverage: true,
  },
  tiktok: {
    description: 'Publishes videos and reports views, followers and engagement.',
    agentPrompt: 'How are my TikTok videos trending on views and followers this month?',
    publishes: true,
    syncMode: 'scheduled',
    dailyCoverage: true,
  },
  pinterest: {
    description: 'Reports impressions, saves and clicks across your pins. Analytics only.',
    agentPrompt: 'Which of my pins are getting the most saves and outbound clicks?',
    publishes: false,
    syncMode: 'scheduled',
    dailyCoverage: true,
  },
  youtube: {
    description: 'Reports channel and video performance. Analytics only.',
    agentPrompt: 'Which of my videos held attention the longest, and what do they share?',
    publishes: false,
    syncMode: 'scheduled',
    dailyCoverage: true,
  },
  shopify: {
    description: 'Syncs orders, revenue and your product catalogue for grounded product content.',
    agentPrompt: 'Which products are selling best right now, and what should I post about them?',
    publishes: false,
    syncMode: 'scheduled',
    dailyCoverage: true,
  },
  'google-analytics': {
    description: 'Syncs sessions, traffic sources and audience data from your website.',
    agentPrompt: 'Which traffic sources are actually converting on my site?',
    publishes: false,
    syncMode: 'scheduled',
    dailyCoverage: true,
  },
  'meta-ads': {
    description: 'Connects selected Meta ad accounts for paid campaign reporting.',
    agentPrompt:
      'Which of my Meta Ads campaigns are spending the most, and what should I investigate first?',
    publishes: false,
    syncMode: 'scheduled',
    dailyCoverage: false,
  },
  'google-ads': {
    description: 'Syncs selected Google Ads customers, campaign performance, and ad spend.',
    agentPrompt:
      'Which Google Ads campaigns are wasting spend, and where should I investigate first?',
    publishes: false,
    syncMode: 'scheduled',
    dailyCoverage: false,
  },
  'linkedin-ads': {
    description: 'Reads one selected LinkedIn ad account for campaign delivery and spend.',
    agentPrompt:
      'Which LinkedIn Ads campaigns delivered over this window, and what should I investigate?',
    publishes: false,
    // Was 'manual' — accurate at the time, because no recurring job existed and
    // this flag is a claim about the running system, not an aspiration. The job
    // now exists: `sync-linkedin-ads-analytics-6h` runs
    // `sync_linkedin_ads_selected_accounts()` every 6 hours, re-reading a
    // 14-day trailing window per selected ad account and advancing
    // `integration_selected_resources.last_synced_at`. Verified end to end
    // against a real ad account on 2026-09-09.
    syncMode: 'scheduled',
    dailyCoverage: false,
  },
  'google-drive': {
    description: 'Imports files from Drive into your media library. Nothing syncs on a schedule.',
    publishes: false,
    syncMode: 'none',
    dailyCoverage: false,
  },
  slack: {
    description:
      'Sends links and files shared in chosen channels into Sources, and lets Mora send messages back to them.',
    publishes: false,
    syncMode: 'none',
    dailyCoverage: false,
  },
};

const FALLBACK: IntegrationDetailSpec = {
  description: 'Connected to Mora.',
  publishes: false,
  syncMode: 'none',
  dailyCoverage: false,
};

/**
 * Resolve the detail shape for a channel. An unknown platform gets the minimal
 * shape — identity and disconnect only — rather than a guessed set of sections.
 */
export function getIntegrationDetailSpec(platform: string): IntegrationDetailSpec {
  return SPECS[platform] ?? FALLBACK;
}
